import { useState } from "react";
import { toast } from "sonner";
import {
  Mail, MapPin, Phone, Clock, Send, CheckCircle2, Loader2,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { COMPANY, SERVICE_OPTIONS } from "@/data/site";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initial = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: null }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!EMAIL_RE.test(form.email)) er.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 7) er.phone = "Please enter a valid phone number.";
    if (!form.service) er.service = "Please choose a service.";
    if (form.message.trim().length < 10) er.message = "Please tell us a little more (at least 10 characters).";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please check the form and try again.");
      setSent(true);
      setForm(initial);
      toast.success("Enquiry sent — we will be in touch within one working day.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = (key) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#0B1F3A] placeholder:text-slate-400 transition-colors focus:border-blue-600 focus:outline-none ${
      errors[key] ? "border-red-400" : "border-slate-200"
    }`;

  return (
    <>
      <Seo
        title="Contact LKTH LTD | Free IT Consultation — Sheffield, UK"
        description="Contact LKTH LTD for a free consultation. Call 07383 390922, email Lateshkatravath3366@gmail.com or send an enquiry. Unit 16 Park Village, Norfolk Park Road, Sheffield S2 2UA."
      />

      <section className="relative overflow-hidden bg-[#0B1F3A] pt-[72px]">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="container-x relative py-16 sm:py-24">
          <p className="eyebrow eyebrow-light font-mono-alt">Contact Us</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold text-white sm:text-5xl">
            Let's talk about your project
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">
            Call, email or send the form below — you will get a reply from a real engineer within
            one working day.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 sm:py-20">
        <div className="container-x grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          {/* Form */}
          <Reveal>
            <div className="card-soft p-7 sm:p-10" data-testid="contact-form">
              {sent ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success-panel">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" aria-hidden="true" />
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-bold text-[#0B1F3A]">Thank you — message received</h2>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Your enquiry has been sent to our team and stored securely. We aim to respond
                    within one working day.
                  </p>
                  <button onClick={() => setSent(false)} className="btn-outline mt-8" data-testid="contact-send-another-btn">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <h2 className="font-display text-xl font-bold text-[#0B1F3A]">Send us an enquiry</h2>
                  <p className="mt-1.5 text-sm text-slate-500">Fields marked * are required.</p>

                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Full name *</label>
                      <input id="cf-name" data-testid="contact-input-name" className={inputCls("name")} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                      {errors.name && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-name">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Email address *</label>
                      <input id="cf-email" type="email" data-testid="contact-input-email" className={inputCls("email")} placeholder="jane@company.co.uk" value={form.email} onChange={set("email")} />
                      {errors.email && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-email">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-phone" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Phone number *</label>
                      <input id="cf-phone" type="tel" data-testid="contact-input-phone" className={inputCls("phone")} placeholder="07xxx xxxxxx" value={form.phone} onChange={set("phone")} />
                      {errors.phone && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-phone">{errors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-company" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Company</label>
                      <input id="cf-company" data-testid="contact-input-company" className={inputCls("company")} placeholder="Your company (optional)" value={form.company} onChange={set("company")} />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-service" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Service needed *</label>
                      <select id="cf-service" data-testid="contact-input-service" className={inputCls("service")} value={form.service} onChange={set("service")}>
                        <option value="">Select a service…</option>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.service && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-service">{errors.service}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-message" className="mb-1.5 block text-sm font-medium text-[#0B1F3A]">Your message *</label>
                      <textarea id="cf-message" rows={5} data-testid="contact-input-message" className={inputCls("message")} placeholder="Tell us about your project, goals and timescales…" value={form.message} onChange={set("message")} />
                      {errors.message && <p className="mt-1.5 text-xs text-red-600" data-testid="contact-error-message">{errors.message}</p>}
                    </div>
                  </div>

                  <button type="submit" disabled={sending} className="btn-primary mt-7 w-full disabled:opacity-60 sm:w-auto" data-testid="contact-form-submit-btn">
                    {sending ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="h-4 w-4" /> Send Enquiry</>
                    )}
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-slate-400">
                    By submitting this form you agree to our processing of your details in line with
                    our Privacy Policy. We never share your information with third parties.
                  </p>
                </form>
              )}
            </div>
          </Reveal>

          {/* Info cards */}
          <div className="space-y-4">
            {[
              { icon: Phone, label: "Call us", value: COMPANY.phoneDisplay, href: COMPANY.phoneHref, testid: "contact-card-phone" },
              { icon: Mail, label: "Email us", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-card-email" },
              { icon: Clock, label: "Business hours", value: COMPANY.hours, testid: "contact-card-hours" },
              {
                icon: MapPin, label: "Visit us", value: COMPANY.addressLines.join(", "),
                href: COMPANY.mapLink, testid: "contact-card-address",
              },
            ].map((c, i) => (
              <Reveal key={c.label} delay={0.06 * i}>
                <div className="card-soft flex items-start gap-4 p-6">
                  <span className="icon-tile shrink-0"><c.icon className="h-5 w-5" aria-hidden="true" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 font-mono-alt">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="mt-1.5 block break-words text-sm font-semibold text-[#0B1F3A] transition-colors hover:text-blue-600" data-testid={c.testid}>
                        {c.value}
                      </a>
                    ) : (
                      <p className="mt-1.5 text-sm font-semibold text-[#0B1F3A]" data-testid={c.testid}>{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Map */}
        <Reveal className="container-x mt-12">
          <div className="card-soft overflow-hidden p-0">
            <iframe
              title="LKTH LTD office location — Unit 16 Park Village, Norfolk Park Road, Sheffield S2 2UA"
              src={COMPANY.mapEmbed}
              className="h-[380px] w-full border-0"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-embed"
            />
          </div>
        </Reveal>
      </section>
    </>
  );
}
